import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Building2, Check, ChevronDown, Loader2, Plus, Wrench } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
// The ui/index.ts barrel does not re-export Dialog.
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { cn } from "../../lib/utils";
import {
  filterMemberships,
  groupMemberships,
  isCurrent,
  type CurrentEntity,
  type Membership,
} from "./switcherLogic";

type Props = {
  memberships: Membership[];
  truncated: boolean;
  current: CurrentEntity;
  /** Menu up to eight entries, dialog above that and on mobile (D10). */
  variant: "menu" | "dialog";
  /** Open state is the header's: it has to close every surface when a switch starts. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * The switch itself lives in `Header` (code review finding 2). Two instances
   * of this component are mounted — the pill and the mobile picker — and a
   * local busy flag left the other one live, so a second `/select` could race
   * the first over the cookie and the navigation.
   */
  onSelect: (row: Membership) => void;
  /** True while *any* header switch control is working. */
  switching: boolean;
  /** «Новая организация…» — the existing /select-role flow. */
  onNewOrganisation: () => void;
  /** Pill label, rendered as the trigger. Absent for the mobile picker. */
  trigger?: React.ReactNode;
};

/**
 * The organisation switcher's surfaces (ticket 19): one list, three triggers —
 * the header pill as a menu, the header pill as a dialog, and the mobile
 * user-menu item that opens the same dialog. Rendering and filtering only; the
 * switch itself belongs to `Header`, which is the one place that can disable
 * every competing control while it runs.
 */
export function OrgSwitcher({
  memberships,
  truncated,
  current,
  variant,
  open,
  onOpenChange,
  onSelect,
  switching,
  onNewOrganisation,
  trigger,
}: Props) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");

  const isOpen = open;
  const setOpen = (next: boolean) => {
    onOpenChange(next);
    if (!next) setQuery("");
  };

  const visible = useMemo(
    () => (variant === "dialog" ? filterMemberships(memberships, query) : memberships),
    [memberships, query, variant]
  );
  const { buildings, providers } = groupMemberships(visible);

  const Row = ({ row, inDialog }: { row: Membership; inDialog: boolean }) => {
    const Icon = row.entityType === "building" ? Building2 : Wrench;
    const active = isCurrent(row, current);
    const showAddress = !!row.address && row.address !== row.name;
    const body = (
      <>
        <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-sm" title={row.name}>
            {row.name}
          </span>
          {showAddress && (
            <span className="truncate text-xs text-muted-foreground" title={row.address!}>
              {row.address}
            </span>
          )}
        </span>
        {active && <Check className="h-4 w-4 shrink-0 text-primary" />}
      </>
    );

    if (inDialog) {
      return (
        <button
          type="button"
          disabled={active || switching}
          onClick={() => onSelect(row)}
          className={cn(
            "flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left",
            active ? "cursor-default opacity-60" : "hover:bg-accent"
          )}>
          {body}
        </button>
      );
    }
    return (
      <DropdownMenuItem
        disabled={active || switching}
        onClick={() => onSelect(row)}
        className="items-start gap-2">
        {body}
      </DropdownMenuItem>
    );
  };

  const groups = (inDialog: boolean) => (
    <>
      {buildings.length > 0 && (
        <>
          {inDialog ? (
            <div className="px-2 pt-2 pb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {t("userMenu.switcher.buildings")}
            </div>
          ) : (
            <DropdownMenuLabel>{t("userMenu.switcher.buildings")}</DropdownMenuLabel>
          )}
          {buildings.map((row) => (
            <Row key={`building:${row.entityId}`} row={row} inDialog={inDialog} />
          ))}
        </>
      )}
      {providers.length > 0 && (
        <>
          {inDialog ? (
            <div className="px-2 pt-2 pb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {t("userMenu.switcher.providers")}
            </div>
          ) : (
            <DropdownMenuLabel>{t("userMenu.switcher.providers")}</DropdownMenuLabel>
          )}
          {providers.map((row) => (
            <Row key={`provider:${row.entityId}`} row={row} inDialog={inDialog} />
          ))}
        </>
      )}
    </>
  );

  if (variant === "menu") {
    return (
      <DropdownMenu open={isOpen} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={t("userMenu.switcher.aria")}
            disabled={switching}
            className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-muted/50 rounded-lg border border-border hover:bg-muted transition-colors">
            {trigger}
            {switching ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
            ) : (
              <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-72">
          {groups(false)}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onNewOrganisation} className="gap-2">
            <Plus className="h-4 w-4" />
            {t("userMenu.switcher.newOrg")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <>
      {trigger && (
        <button
          type="button"
          aria-label={t("userMenu.switcher.aria")}
          disabled={switching}
          onClick={() => setOpen(true)}
          className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-muted/50 rounded-lg border border-border hover:bg-muted transition-colors">
          {trigger}
          {switching ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
          ) : (
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
          )}
        </button>
      )}
      <Dialog open={isOpen} onOpenChange={setOpen}>
        <DialogContent className="max-h-[80vh] gap-3 overflow-hidden">
          <DialogHeader>
            <DialogTitle>{t("userMenu.switcher.changeOrg")}</DialogTitle>
          </DialogHeader>
          <Input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("userMenu.switcher.search")}
            aria-label={t("userMenu.switcher.search")}
          />
          {truncated && (
            <p className="text-xs text-muted-foreground">
              {t("userMenu.switcher.truncated", { count: memberships.length })}
            </p>
          )}
          <div className="-mx-1 max-h-[50vh] overflow-y-auto px-1">
            {visible.length === 0 ? (
              <p className="px-2 py-6 text-center text-sm text-muted-foreground">
                {t("userMenu.switcher.noMatches")}
              </p>
            ) : (
              groups(true)
            )}
          </div>
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              onNewOrganisation();
            }}
            className="flex w-full items-center gap-2 rounded-lg border border-border px-2 py-2 text-left text-sm hover:bg-accent">
            <Plus className="h-4 w-4" />
            {t("userMenu.switcher.newOrg")}
          </button>
        </DialogContent>
      </Dialog>
    </>
  );
}
